import { useEffect, useState } from "react";
import BaseWrapper from "../../../components/ui/BaseWrapper";
import { useMutation, useQuery } from "@tanstack/react-query";
import { clearAuthStorage, navigateWithReload, formatPhoneNumber } from "../../../utils/helpers";
import BaseLoader from "../../../components/ui/BaseLoader";

import { useDispatch } from "react-redux";
import { showToast } from "../../../store/toast/toastSlice";
import { useNavigate } from "react-router-dom";

import BaseDataTableV2 from "../../../components/ui/BaseDataTableV2";
import BaseDropdownV2 from "../../../components/ui/BaseDropdownV2";
import { deleteProject, fetchAllProjects } from "../../../services/api/project";
import useAuth from "../../../hooks/useAuth";
import BaseConfirmModal from "../../../components/ui/BaseConfirmModal";

const ProjectTable = () => {
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const { getPermissionForModule } = useAuth();
  const {
    hasUpdateAccess: hasProjectUpdateAccess,
    hasReadAccess: hasProjectReadAccess,
    hasDeleteAccess,
  } = getPermissionForModule("CLIENT_PROJECT");
  const {
    hasUpdateAccess: hasClientUpdateAccess,
    hasReadAccess: hasClientReadAccess,
  } = getPermissionForModule("CLIENT_CUSTOMER");
  const [openDropdownId, setOpenDropdownId] = useState(null); // Track open dropdown by work order ID
  const baseQueryParams = {
    limit: 20,
    page: 1,
    search: "",
  };
  const [queryParams, setQueryParams] = useState(baseQueryParams);
  const [paginationModel, setPaginationModel] = useState({
    page: 0,
    pageSize: 20,
  });
  const [rowCount, setRowCount] = useState(0);
  const [projectsList, setProjectsList] = useState([]);
  const [menuOptions, setMenuOptions] = useState([]);
  const [isDeleteConfirmationModalOpen, setIsDeleteConfirmationModalOpen] =
    useState(false);
  const [selectedProject, setSelectedProject] = useState(null);

  const {
    isPending: isFetchAllProjectsLoadingApi,
    error: isFetchAllClientsErrorApi,
    refetch: refetchAllProjects,
    data: projectsData,
  } = useQuery({
    queryKey: ["projects", queryParams],
    queryFn: () => fetchAllProjects(queryParams),
  });

  const {
    isPending: isDeleteProjectLoading,
    status,
    error,
    mutateAsync: deleteProjectMutation,
  } = useMutation({
    mutationFn: deleteProject,
  });

  if (isFetchAllClientsErrorApi?.response?.status === 401) {
    clearAuthStorage();
    navigateWithReload("/login");
  }
  useEffect(() => {
    if (projectsData?.data?.projects) {
      setProjectsList(projectsData?.data?.projects);
      setRowCount(projectsData?.data?.totalCount);
    }
  }, [projectsData]);

  useEffect(() => {
    const options = [];

    if (hasProjectUpdateAccess) options.push({ label: "Edit", value: "EDIT" });
    if (hasDeleteAccess) options.push({ label: "Delete", value: "DELETE" });

    setMenuOptions(options);
  }, [hasProjectUpdateAccess, hasDeleteAccess]);

  const columns = [
    {
      pinLeft: true,
      field: "id",
      headerName: "ID",
      flex: 1,
      renderCell: (params) => (
        <p
          // onContextMenu={(e) => handleContextMenu(e, params)} // Right-click triggers context menu
          className={`table-font-size font-normal cursor-pointer ${
            hasProjectUpdateAccess || hasProjectReadAccess
              ? "text-primary"
              : "text-gray-500"
          }`}
          onClick={() => {
            if (hasProjectUpdateAccess || hasProjectReadAccess) {
              navigate(`edit/${params?.id}`);
            }
          }}
        >
          {params?.value}
        </p>
      ),
    },

    {
      field: "title",
      headerName: "Project Name",
      flex: 1,
      renderCell: (params) => (
        <p
          onClick={() => {
            if (hasProjectUpdateAccess || hasProjectReadAccess) {
              navigate(`edit/${params?.id}`);
            }
          }}
          className={`table-font-size truncate max-w-64 overflow-hidden whitespace-nowrap cursor-pointer ${
            hasProjectUpdateAccess || hasProjectReadAccess
              ? "text-primary"
              : "text-gray-500"
          }`}
        >
          {params.value || "-"}
        </p>
      ),
    },

    {
      field: "client",
      headerName: "Client",
      flex: 1,
      renderCell: (params) => {
        return (
          <p
            className={`table-font-size cursor-pointer ${
              hasClientUpdateAccess || hasClientReadAccess
                ? "text-primary"
                : "text-gray-500"
            }`}
            onClick={() => {
              if (hasClientUpdateAccess || hasClientReadAccess) {
                navigate(`/workBoard/clients/edit/${params?.value?.id}`);
              }
            }}
          >
            {params?.value?.clientName || "-"}
          </p>
        );
      },
    },

    {
      field: "managerName",
      headerName: "Manager",
      flex: 1,
      renderCell: (params) => {
        return (
          <p className="table-font-size  cursor-pointer">
            {params?.value || "-"}
          </p>
        );
      },
    },

    {
      field: "phoneNumber",
      flex: 1,
      headerName: "Contact",
      renderCell: (params) => {
        return (
          <p className="table-font-size  cursor-pointer">
            {formatPhoneNumber(params?.value) || "-"}
          </p>
        );
      },
    },

    (hasProjectUpdateAccess || hasDeleteAccess) && {
      headerName: "Actions",
      sortable: false,
      filterable: false,
      renderCell: (params) => (
        <BaseDropdownV2
          menuOptions={menuOptions}
          triggerElement={
            <i class="fa-solid fa-ellipsis cursor-pointer ml-4 text-base"></i>
          }
          onItemClick={(value) => handleItemClick(value, params?.row)}
        />
      ),
    },
  ].filter(Boolean);

  const handleItemClick = (value, params) => {
    if (value === "EDIT") {
      navigate(`edit/${params?.id}?mode=DIRECT`);
    }
    if (value === "DELETE") {
      setSelectedProject({ id: params?.id, projectName: params?.title });
      setIsDeleteConfirmationModalOpen(true);
    }
  };

  const handleDeleteProject = async (projectId) => {
    try {
      const payload = {
        id: projectId,
      };
      const response = await deleteProjectMutation(projectId);
      setOpenDropdownId(null); // Close the dropdown if it's already open
      dispatch(
        showToast({
          message: "Project deleted successfully",
          type: "success",
        })
      );
      setIsDeleteConfirmationModalOpen(false);
      refetchAllProjects();
    } catch (error) {
      dispatch(
        showToast({
          message: error?.response?.data?.message,
          type: "error",
        })
      );
    }
  };

  const handlePaginationChange = (newPaginationModel) => {
    setPaginationModel(newPaginationModel);

    setQueryParams((prev) => ({
      ...prev,
      page: newPaginationModel.page + 1,
      limit: newPaginationModel.pageSize,
    }));
  };
  return (
    <>
      {isFetchAllProjectsLoadingApi && (
        <BaseLoader isLoading={isFetchAllProjectsLoadingApi} />
      )}
      <BaseWrapper>
        <div className="bg-white rounded-lg px-5 py-3.5 shadow-md my-4 sticky top-10 z-10">
          <div className="flex">
            <div className="flex items-center border-r-[1px] border-[#8C8C8C] pr-5">
              <span className="flex items-center justify-center w-7 h-7 rounded-full bg-primary"><i className="fa-light fa-filter text-white text-sm"></i></span>
              <p className="text-primary ml-2 ">Add Filter</p>
            </div>
          </div>
        </div>

            <BaseDataTableV2
              className="max-h-[calc(100vh-300px)] overflow-auto"
              rowHeight={38}
              columns={columns}
              data={projectsList}
              isLoading={false}
              disableColumnResize={false}
              checkboxSelection
              hideFooter={false}
              pagination
              paginationMode="server"
              paginationModel={paginationModel}
              onPaginationModelChange={handlePaginationChange}
              pageSizeOptions={[10, 20, 50, 100]}
              rowCount={rowCount}
            />
        {isDeleteConfirmationModalOpen && (
          <BaseConfirmModal
            open={isDeleteConfirmationModalOpen}
            onClose={() => setIsDeleteConfirmationModalOpen(false)}
            cancelText="Cancel"
            confirmText="Delete"
            confirmButtonColor="error"
            title="Delete Project"
            description={
              <p>
                Are you sure you want to delete the project
                {
                  <span className="text-red-600 font-medium mx-1 capitalize">
                    "{selectedProject?.projectName}"
                  </span>
                }
                ? This action cannot be undone.
              </p>
            }
            onConfirm={() => handleDeleteProject(selectedProject?.id)}
          />
        )}
      </BaseWrapper>
    </>
  );
};

export default ProjectTable;
